// What a launch actually did, said in words, next to the button that started it.
//
// This used to render below the whole creatives table, where the operator was
// not looking: clicking Launch cleared the checkboxes and appeared to do
// nothing else. The card now sits under the launch bar and is the ONLY place
// on this tab that reports a launch. (The History tab is a different question
// — every launch ever, not the one just started.)
import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { adUrl, adsetUrl } from './adsManager';
import { BatchStatus, LaunchRow, QueuedBatch } from './types';

export type OutcomeKind = 'queued' | 'uploading' | 'creating' | 'done' | 'partial' | 'failed';

export interface Outcome {
  kind: OutcomeKind;
  /** One line the operator can act on without reading the per-ad list. */
  text: string;
  settled: boolean;
}

/**
 * The batch's state in one sentence.
 *
 * `done` is deliberately NOT the success signal: the API sets it once every row
 * is DONE **or FAILED**, so a batch where every single ad failed arrives here
 * with `done: true`. Reading success off `done` would report a total failure as
 * a launch.
 */
export function batchOutcome(status: BatchStatus | undefined, queued?: QueuedBatch): Outcome {
  const total = status?.launches.length ?? queued?.count ?? 0;
  const adStatus = status?.launches[0]?.ad_status || queued?.adStatus || 'PAUSED';

  if (!status) return { kind: 'queued', text: 'Queued — waiting for the launch worker.', settled: false };

  if (!status.done) {
    // The ad set is created once, after every asset has been uploaded, so its
    // absence is what separates "still moving files" from "making ads".
    const hasAdset = status.launches.some(l => l.adset_id);
    if (!hasAdset) {
      const started = status.launches.some(l => l.status === 'RUNNING');
      return started
        ? { kind: 'uploading', text: 'Uploading assets and creating the ad set…', settled: false }
        : { kind: 'queued', text: 'Queued — the launch worker has not picked this up yet.', settled: false };
    }
    const finished = status.launches.filter(l => l.status === 'DONE' || l.status === 'FAILED').length;
    return { kind: 'creating', text: `Creating ads (${finished} of ${total})…`, settled: false };
  }

  const { succeeded, failed } = status;
  if (!succeeded) {
    return { kind: 'failed', text: `Nothing was created — all ${failed} failed.`, settled: true };
  }
  const ads = `${succeeded} ${succeeded === 1 ? 'ad' : 'ads'} created, ${adStatus}`;
  return failed
    ? { kind: 'partial', text: `${ads} — ${failed} failed.`, settled: true }
    : { kind: 'done', text: `${ads}.`, settled: true };
}

/**
 * Creatives whose ad is live but whose Notion row could not be tagged.
 *
 * They are still hidden from the table — the ad is real, and continuing to
 * offer it for launch is worse than a warning — but Notion is what the
 * candidates list reads, so an untagged row comes back on the next reload.
 * Naming them is the only way the operator knows to go fix it.
 */
export function untaggedNames(status: BatchStatus | undefined): string[] {
  return (status?.launches ?? [])
    .filter(l => l.status === 'DONE' && !l.notion_marked)
    .map(l => l.creative_name);
}

/** Every notion page whose ad was created — what the table should stop offering. */
export function launchedPageIds(status: BatchStatus | undefined): string[] {
  return (status?.launches ?? []).filter(l => l.status === 'DONE').map(l => l.notion_page_id);
}

/** The ad set the batch created, once it exists. */
export function batchAdsetId(status: BatchStatus | undefined): string | null {
  return (status?.launches ?? []).find(l => l.adset_id)?.adset_id ?? null;
}

const SEVERITY: Record<OutcomeKind, 'info' | 'success' | 'warning' | 'error'> = {
  queued: 'info',
  uploading: 'info',
  creating: 'info',
  done: 'success',
  partial: 'warning',
  failed: 'error',
};

function RowLine({ row, accountId }: { row: LaunchRow; accountId?: string }) {
  const live = row.status === 'DONE' && row.meta_ad_id && accountId;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {row.status === 'DONE' && <CheckCircleIcon fontSize="small" color="success" />}
      {row.status === 'FAILED' && <ErrorOutlineIcon fontSize="small" color="error" />}
      {(row.status === 'PENDING' || row.status === 'RUNNING') && <CircularProgress size={14} />}
      <Typography variant="body2" sx={{ minWidth: 180 }} noWrap>{row.creative_name}</Typography>
      {live ? (
        // The ad, with its ad set and campaign selected around it — opening an
        // ad on its own gives no clue which batch or campaign it belongs to.
        <Link
          variant="caption"
          href={adUrl(accountId as string, row.meta_ad_id as string, {
            campaignId: row.campaign_id,
            adsetId: row.adset_id,
          })}
          target="_blank"
          rel="noopener"
        >
          ad {row.meta_ad_id}
        </Link>
      ) : (
        <Typography variant="caption" color={row.status === 'FAILED' ? 'error' : 'text.secondary'}>
          {row.status === 'DONE' ? `ad ${row.meta_ad_id}` : row.error_message || row.status.toLowerCase()}
        </Typography>
      )}
    </Stack>
  );
}

export default function BatchResult({
  batchId,
  queued,
  status,
  accountId,
}: {
  batchId: string;
  queued?: QueuedBatch;
  status?: BatchStatus;
  accountId?: string;
}) {
  const outcome = batchOutcome(status, queued);
  const untagged = untaggedNames(status);
  const adsetId = batchAdsetId(status);
  const campaignId = status?.launches[0]?.campaign_id ?? null;
  // Named from the queue response so the card can say WHICH batch this is the
  // moment Launch resolves, a poll before any ledger row comes back.
  const adsetName = queued?.adsetName || status?.launches[0]?.adset_name || 'Batch';
  const campaign = queued?.campaign.name || status?.launches[0]?.campaign_name;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }} data-batch-id={batchId}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{adsetName}</Typography>
        {campaign && <Chip size="small" variant="outlined" label={campaign} />}
        {!outcome.settled && <CircularProgress size={14} />}
        <Box sx={{ flex: 1 }} />
        {adsetId && accountId && (
          <Button
            size="small"
            variant={outcome.settled ? 'contained' : 'text'}
            endIcon={<OpenInNewIcon />}
            href={adsetUrl(accountId, adsetId, campaignId)}
            target="_blank"
            rel="noopener"
          >
            Open in Ads Manager
          </Button>
        )}
      </Stack>

      <Alert severity={SEVERITY[outcome.kind]} sx={{ mb: untagged.length || status ? 1 : 0 }}>
        {outcome.text}
      </Alert>

      {untagged.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Created, but {untagged.length === 1 ? 'this row was' : `these ${untagged.length} rows were`} not
          tagged Launched in Notion: {untagged.join(', ')}. Tag {untagged.length === 1 ? 'it' : 'them'} by
          hand, or {untagged.length === 1 ? 'it comes' : 'they come'} back to this list on the next reload.
        </Alert>
      )}

      {status?.launches.map(row => <RowLine key={row.launch_id} row={row} accountId={accountId} />)}
    </Paper>
  );
}
